import { useCallback, useState } from 'react';
import { type SwipeEventData, useSwipeable } from 'react-swipeable';

interface UseMessageSwipeOptions {
  /** Callback when swiping right (reply action) */
  onSwipeRight?: () => void;
  /** Callback when swiping left (show actions) */
  onSwipeLeft?: () => void;
  /** Whether swipe gestures are enabled */
  enabled?: boolean;
  /** Minimum swipe distance threshold in pixels */
  threshold?: number;
}

interface UseMessageSwipeReturn {
  /** Spread these handlers on the swipeable element */
  handlers: ReturnType<typeof useSwipeable>;
  /** Current swipe offset for visual feedback */
  swipeOffset: number;
  /** Whether currently swiping */
  isSwiping: boolean;
}

/**
 * Hook for handling swipe gestures on messages (mobile)
 * - Swipe right: Quick reply
 * - Swipe left: Show message actions
 */
export function useMessageSwipe({
  onSwipeRight,
  onSwipeLeft,
  enabled = true,
  threshold = 50
}: UseMessageSwipeOptions = {}): UseMessageSwipeReturn {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const handleSwiping = useCallback(
    (eventData: SwipeEventData) => {
      if (!enabled) return;
      setIsSwiping(true);
      // Clamp offset for visual feedback
      const clampedOffset = Math.max(-100, Math.min(100, eventData.deltaX));
      setSwipeOffset(clampedOffset);
    },
    [enabled]
  );

  const handleSwiped = useCallback(() => {
    setIsSwiping(false);
    setSwipeOffset(0);
  }, []);

  const handlers = useSwipeable({
    onSwipedRight: () => {
      if (enabled && onSwipeRight) onSwipeRight();
    },
    onSwipedLeft: () => {
      if (enabled && onSwipeLeft) onSwipeLeft();
    },
    onSwiping: handleSwiping,
    onSwiped: handleSwiped,
    preventScrollOnSwipe: true,
    trackMouse: false,
    delta: threshold
  });

  return { handlers, swipeOffset, isSwiping };
}
